import {i18n, useTranslation} from "next-i18next";
import {useState} from "react";
import {ScreenNumber, useMediaQuery} from "../../hooks/useMediaQuery";
import {requestsCreationAttributes} from "../../models/requests";
import {DropdownButtonSubMenuItem} from "../common/DropdownButton";
import FormDropdown from "../common/FormDropdown";

export enum FormFieldTypeEnum {
	text = "text",
	dropdown = "dropdown",
	email = "email",
	tel = "tel",
	number = "number"
}

export interface FormField {
	label: string;
	type: FormFieldTypeEnum;
	placeholder: string;
	colSpan: 1 | 2 | 3 | 4 | 5;
	autoComplete?: string;
	required?: boolean;
	requestKey: keyof requestsCreationAttributes;
	defaultValue?: string;
}

export interface DropdownFormField extends Omit<FormField, "autoComplete"> {
	type: FormFieldTypeEnum.dropdown;
	dropdownOptions: DropdownButtonSubMenuItem[];
}

export interface OtherFormField extends Omit<FormField, "dropdownOptions"> {
	type: Exclude<FormFieldTypeEnum, FormFieldTypeEnum.dropdown>;
}

export type FormFieldType = DropdownFormField | OtherFormField;

export const ContactFormFields: FormFieldType[] = [
	{
		label: "forms.salutation",
		type: FormFieldTypeEnum.dropdown,
		placeholder: "forms.placeholders.salutation",
		dropdownOptions: [
			{
				name: "forms.gender.male"
			},
			{
				name: "forms.gender.female"
			},
			{
				name: "forms.gender.neutral"
			}
		],
		colSpan: 1,
		required: true,
		requestKey: "title"
	},
	{
		label: "forms.firstname",
		type: FormFieldTypeEnum.text,
		placeholder: "forms.placeholders.firstname",
		colSpan: 2,
		autoComplete: "given-name",
		required: true,
		requestKey: "firstname"
	},
	{
		label: "forms.lastname",
		type: FormFieldTypeEnum.text,
		placeholder: "forms.placeholders.lastname",
		colSpan: 2,
		autoComplete: "family-name",
		required: true,
		requestKey: "lastname"
	},
	{
		type: FormFieldTypeEnum.tel,
		label: "forms.phone",
		placeholder: "forms.placeholders.phone",
		colSpan: 3,
		autoComplete: "tel",
		requestKey: "phone",
		defaultValue: "+49"
	},
	{
		label: "forms.email",
		type: FormFieldTypeEnum.email,
		placeholder: "forms.placeholders.email",
		colSpan: 2,
		autoComplete: "email",
		required: true,
		requestKey: "email"
	},
	{
		label: "forms.identityNumber",
		type: FormFieldTypeEnum.text,
		placeholder: "forms.placeholders.identityNumber",
		colSpan: 2,
		required: false,
		requestKey: "customer_id"
	},
	{
		label: "forms.street",
		type: FormFieldTypeEnum.text,
		placeholder: "forms.placeholders.street",
		colSpan: 2,
		autoComplete: "street-address",
		requestKey: "street"
	},
	{
		label: "forms.housenumber",
		type: FormFieldTypeEnum.number,
		placeholder: "forms.placeholders.housenumber",
		colSpan: 1,
		requestKey: "number"
	},
	{
		label: "forms.postalcode",
		type: FormFieldTypeEnum.number,
		placeholder: "forms.placeholders.postalcode",
		colSpan: 2,
		autoComplete: "postal-code",
		requestKey: "postcode"
	},
	{
		label: "forms.city",
		type: FormFieldTypeEnum.text,
		placeholder: "forms.placeholders.city",
		colSpan: 2,
		autoComplete: "address-level2",
		requestKey: "city"
	}
	// {
	//   label: "forms.country",
	//   type: FormFieldTypeEnum.dropdown,
	//   placeholder: "forms.placeholders.country",
	//   dropdownOptions: [
	//     {
	//       name: "forms.countries.germany"
	//     },
	//     {
	//       name: "forms.countries.austria"
	//     },
	//     {
	//       name: "forms.countries.switzerland"
	//     },
	//     {
	//       name: "forms.countries.canada"
	//     },
	//     {
	//       name: "forms.countries.usa"
	//     }
	//   ],
	//   colSpan: 2,
	//   // TODO: country has to be converted to language code (de, en, etc.)
	//   requestKey: "languagecode"
	// }
];

const SmallVersionFields = [
	"forms.salutation",
	"forms.firstname",
	"forms.lastname",
	"forms.phoneCountryCode",
	"forms.phone",
	"forms.email"
	// "forms.country"
];
const formFieldsSmallVersion: FormFieldType[] = ContactFormFields.filter((c) =>
	SmallVersionFields.includes(c.label)
);

// TODO: change non required fields to optional
export interface ContactFormInterface {
	title: "Herr" | "Frau" | "Divers";
	firstname: string;
	lastname: string;
	phone?: "+49" | "+43" | "+99";
	mobile?: number;
	email: string;
	customer_id: number;
	street?: string;
	number?: number;
	postcode?: number;
	city?: string;
	languagecode?: "de" | "Österreich" | "Schweiz" | "Kanada" | "USA";
	message?: string;
}

interface ContactFormProps {
	contactForm?: ContactFormInterface;
	onChange: (
		key: keyof requestsCreationAttributes,
		value: string | number
	) => void;
	smallVersion?: boolean;
	showMessage?: boolean;
}

function ContactForm({
	                     contactForm,
	                     onChange,
	                     smallVersion,
						 showMessage = true
                     }: ContactFormProps) {
	const chosenFormFields = smallVersion
		? formFieldsSmallVersion
		: ContactFormFields;
	const {currentScreenAsNumber} = useMediaQuery();
	const {t} = useTranslation();
	const [selectedDropdownItems, setSelectedDropdownItems] = useState<
		{
			selectedOption: DropdownButtonSubMenuItem;
			label: string;
		}[]
	>([
		{
			selectedOption: (
				chosenFormFields.find(
					(formField) => formField.label === "forms.country"
				) as DropdownFormField
			)?.dropdownOptions[0],
			label: "forms.country"
		},
		{
			selectedOption: (
				chosenFormFields.find(
					(formField) => formField.label === "forms.salutation"
				) as DropdownFormField
			)?.dropdownOptions[0],
			label: "forms.salutation"
		},
		{
			selectedOption: (
				chosenFormFields.find(
					(formField) => formField.label === "forms.phoneCountryCode"
				) as DropdownFormField
			)?.dropdownOptions[0],
			label: "forms.phone"
		}
	]);

	const countryCode: Record<string, string> = {
		de: "+49",
		en: "+1",
		fr: "+33",
		nl: "+31",
		ru: "+7",
		es: "+34",
		ca: "+1",
		ua: "+380"
	};

	const [phonePrefix, setPhonePrefix] = useState(49);

	return (
		<div className="grid grid-cols-1 lg:grid-cols-5 gap-x-6 gap-y-6 place-content-stretch place-items-stretch justify-end">
			{chosenFormFields.map((formField, idx) => {
				switch (formField.type) {
					case FormFieldTypeEnum.text:
					case FormFieldTypeEnum.number:
					case FormFieldTypeEnum.email:
						return (
							<div
								key={idx}
								className={`${
									currentScreenAsNumber &&
									currentScreenAsNumber > ScreenNumber.lg
										? `col-span-${formField.colSpan}`
										: "col-span-full"
								} space-y-2`}
							>
								{!formField.label ? (
									<label className="invisible">{"Nothing here"}</label>
								) : (
									<label className="">
										{t(formField.label)}
										{formField.required && "*"}:
									</label>
								)}
								<input
									type={formField.type}
									placeholder={t(formField.placeholder)}
									className="w-full border-none shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)] placeholder-leo-text-gray"
									required={formField.required}
									autoComplete={formField.autoComplete}
									name={formField.autoComplete}
									value={
										contactForm &&
										contactForm[
											formField.requestKey as keyof ContactFormInterface
											]
									}
									onChange={(e) => {
										const value = e.target.value;
										let newValue;
										if (formField.type === FormFieldTypeEnum.number) {
											newValue = parseInt(value);
										}
										onChange(formField.requestKey, newValue ?? value);
									}}
								/>
							</div>
						);
					case FormFieldTypeEnum.dropdown:
						const tmpItem = selectedDropdownItems.find(
							(item) => item.label === formField.label
						);
						const selectedOptionText =
							tmpItem && tmpItem.selectedOption
								? t(tmpItem.selectedOption.name)
								: t(formField.placeholder);

						return (
							<div
								key={idx}
								className={`${
									currentScreenAsNumber &&
									currentScreenAsNumber > ScreenNumber.lg
										? `col-span-${formField.colSpan}`
										: "col-span-full"
								} space-y-2 flex flex-col justify-between`}
							>
								{!formField.label ? (
									<label className="invisible">{"Nothing here"}</label>
								) : (
									<label className="">
										{t(formField.label)}
										{formField.required && "*"}:
									</label>
								)}
								<div className="w-full shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)] h-[40px]">
									<FormDropdown
										menuItems={formField.dropdownOptions}
										text={selectedOptionText}
										onSelectedItem={(selectedItem) => {
											setSelectedDropdownItems((prev) => {
												return prev.map((item) => {
													if (item.label === formField.label) {
														return {
															...item,
															selectedOption: selectedItem
														};
													}
													return item;
												});
											});
											onChange(formField.requestKey, selectedItem.name);
										}}
									/>
								</div>
							</div>
						);
					case FormFieldTypeEnum.tel:
						return (
							<div
								key={idx}
								className={`${
									currentScreenAsNumber &&
									currentScreenAsNumber > ScreenNumber.lg
										? `col-span-2 lg:col-start-2`
										: "col-span-full"
								} space-y-2`}
							>
								{!formField.label ? (
									<label className="invisible">{"Nothing here"}</label>
								) : (
									<label className="">
										{t(formField.label)}
										{formField.required && "*"}:
									</label>
								)}
								<div className="grid grid-cols-3 gap-6">

									<input
										type={"text"}
										key={i18n?.language ?? "default-lang"}
										placeholder={t(formField.placeholder)}
										className="w-full border-none shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)] placeholder-leo-text-gray col-span-3"
										required={formField.required}
										autoComplete={formField.autoComplete}
										name={formField.autoComplete}
										defaultValue={i18n?.language ? countryCode[i18n?.language]?.toUpperCase() : ""}
										value={
											contactForm &&
											contactForm[
												formField.requestKey as keyof ContactFormInterface
												]
										}
										onChange={(e) => {
											onChange(formField.requestKey, e.target.value);
										}}
									/>
								</div>
							</div>
						);
					default:
						return;
				}
			})}

			{showMessage && (
				<div className={`col-span-full`}>
					<label className="">{t("forms.message")}</label>
					<textarea
						name="text"
						rows={10}
						className="w-full border-none shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)] resize-none p-4 placeholder-leo-text-gray"
						placeholder={t("forms.messageHelperText")}
						onChange={(e) => {
							onChange("message", e.target.value);
						}}
					/>
				</div>
			)}
			<div className={`col-span-full lg:col-start-2`}>
				<label className="text-[#85888c] font-normal">* {t("common.requiredField")}</label>
			</div>
		</div>
	);
}

export default ContactForm;
